import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import TSPSDK from 'tsp-sdk';
import { generateTransifyCommon } from '@scfe-common/utils-transify-client';
import { firstLetterUpper, setLanguageToCookie } from '../shared/utils/base';
import { getCurRegion, getCurEnv } from '../shared/utils/multiRegionUtil';
import { LanguageOptions } from './types';
import { uaIsWebview } from '../shared/utils/is-webview';

const project = 'spx-h5';
const NS = 'common';

const targetLocalMap: Record<string, string> = {
  en: 'en',
  tw: 'zh-Hant',
  my: 'ms-MY',
  th: 'th',
  vn: 'vi',
  id: 'id',
  sg: 'sg',
  br: 'pt-BR',
  ph: 'ph',
};

const resourceId = 4164;
const env = process.env;
const currentEnv = getCurEnv();
// eslint-disable-next-line no-console

const getI18nLangCode = (lang: LanguageOptions | string) => (
  {
    'ms-my': 'ms-MY',
    ph: 'en-PH',
  }[lang] || lang
);

/**
 * check current language via url and country
 * stop using localStorage to save user behavior
 * make sure the lang we use is decided by url or language switcher
 */
const defaultLang = 'en';
const tspEnv = currentEnv === 'live' ? 'live' : 'nonLive';
export const region = getCurRegion().toLowerCase();
export const tspLocale = targetLocalMap[region] || 'en';

setLanguageToCookie(defaultLang);

i18next.init({
  lng: region || defaultLang,
  fallbackLng: 'en',
  defaultNS: 'common',
  keySeparator: false,
  debug: env.NODE_ENV === 'development',
  resources: {},
  interpolation: {
    escapeValue: false,
  },
  react: {
    wait: false,
    bindI18n: 'languageChanged loaded',
    bindStore: 'added removed',
    nsMode: 'default',
  } as any,
});

export const changeI18nLanguage = (locale: any) => {
  i18next.changeLanguage(locale);
};

// Uppercase the first letter of every word. abcd => Abcd or abcd efg => Abcd Efg
export const tUpper = (str: any, allWords = true) => firstLetterUpper(i18next.t(str), allWords);

// Uppercase all letters. abcd => ABCD
export const tUpperCase = (str: any) => i18next.t(str).toUpperCase();

const translator = new TSPSDK({
  name: 'website sdk', // optional, just a identifier
});

const getOnlineResource = async (language: string, resourceId: number) => {
  console.log('transify region', region);
  const res = await translator.create({
    env: tspEnv,
    locale: language, // language code, en, vi, th, zh-Hans etc, case insensitive
    translation: {
      resources: [resourceId],
    },
    region,
  });
  return res.translation;
};

export const requestOnlineI18n = async (
  currentLanguage: LanguageOptions,
  resourceId: number,
  i18langCode?: ReturnType<typeof getI18nLangCode>,
) => {
  console.info('i18n online request');
  const res = await getOnlineResource(currentLanguage, resourceId);
  i18next.addResourceBundle(i18langCode || getI18nLangCode(currentLanguage), NS, res, true, true);
};

const loadOfflineI18n = async (currentLanguage: LanguageOptions) => {
  // 需要在打包的时候全局替换__OFFLINE_I18N_REQ_PATH__， 不能使用env变量
  const response = await fetch(
    `//${__OFFLINE_I18N_REQ_PATH__}/${currentLanguage}.json?_offl_id=bundle&_app_id=${__APP_ID__}`,
  );
  return response.json();
};

const applyOfflineI18n = async (
  currentLanguage: LanguageOptions,
  resourceId: number,
  i18langCode: ReturnType<typeof getI18nLangCode>,
) => {
  if (!uaIsWebview) {
    // 非webview直接线上请求
    return requestOnlineI18n(currentLanguage, resourceId, i18langCode);
  }
  try {
    const translation = await loadOfflineI18n(currentLanguage);
    i18next.addResourceBundle(i18langCode, NS, translation, true, true);
    console.info('load offlineI18 successfully---');
  } catch (error) {
    console.error('离线语言包加载失败', error);
    await requestOnlineI18n(currentLanguage, resourceId, i18langCode);
  }
};

export const loadResource = async (lng = tspLocale, resourceIdOuter?: number) => {
  // const translator = new TSPSDK({
  //   name: 'website sdk', // optional, just a identifier
  // });
  // const { translation } = await translator.create({
  //   env: tspEnv, // determine to use live or nonlive api， default live
  //   locale: lng, // language code, en, vi, th, zh-Hans etc, case insensitive
  //   translation: {
  //     // optional, resource id, it will be ignored when there is a projectId parameter
  //     resources: [resourceIdOuter || resourceId],
  //   },
  // });

  // i18next.addResourceBundle(lng, 'common', translation);
  const i18langCode = getI18nLangCode(lng);
  i18next.use(initReactI18next).init({
    fallbackLng: i18langCode,
    lng: i18langCode,
    debug: false,
    resources: {
      [lng]: {
        [NS]: {},
      },
    },
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
      prefix: '{',
      suffix: '}',
    },
    react: {
      bindI18nStore: 'added', // addResourceBundle时候出发重新渲染语言，针对refreshI18nAfterRendered异步更新语言
    },
  });
  await applyOfflineI18n(lng as LanguageOptions, resourceIdOuter || resourceId, i18langCode);
};

export default i18next;

const gt = generateTransifyCommon({
  t: (key, options): any => {
    const val = i18next.t(key, options);
    return val;
  },
  project,
  isShowKey: false,
});

export const $gt = gt;
